import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { projectsAPI, landsAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  FolderKanban,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Calendar,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [lands, setLands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedProject, setExpandedProject] = useState(null);
  const [formData, setFormData] = useState({
    land_id: '',
    title: '',
    description: '',
    project_type: 'new_build',
    total_budget: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [projectsRes, landsRes] = await Promise.all([
        projectsAPI.getAll(),
        landsAPI.getAll(),
      ]);
      setProjects(projectsRes.data);
      setLands(landsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        ...formData,
        total_budget: parseFloat(formData.total_budget) || 0,
      };
      await projectsAPI.create(data);
      toast.success('Project created successfully');
      setDialogOpen(false);
      setFormData({
        land_id: '',
        title: '',
        description: '',
        project_type: 'new_build',
        total_budget: '',
      });
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create project';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStageUpdate = async (projectId, stageId, newStatus) => {
    try {
      await projectsAPI.updateStage(projectId, stageId, { status: newStatus });
      toast.success('Stage updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-[#10B981]" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-[#F59E0B]" />;
      default:
        return <AlertCircle className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-[#10B981] bg-emerald-50';
      case 'in_progress':
        return 'text-[#F59E0B] bg-amber-50';
      case 'planning':
        return 'text-blue-600 bg-blue-50';
      case 'paused':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const calculateProgress = (stages) => {
    if (!stages || stages.length === 0) return 0;
    const completed = stages.filter((s) => s.status === 'completed').length;
    return Math.round((completed / stages.length) * 100);
  };

  const getLandTitle = (landId) => {
    const land = lands.find((l) => l.land_id === landId);
    return land?.title || 'Unknown Land';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="projects-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">Projects</h1>
            <p className="text-muted-foreground mt-1">
              Manage your construction projects and track progress
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-project-btn" disabled={lands.length === 0}>
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">Create New Project</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="land_id">Select Land *</Label>
                  <Select 
                    value={formData.land_id} 
                    onValueChange={(v) => handleSelectChange('land_id', v)}
                  >
                    <SelectTrigger data-testid="land-select">
                      <SelectValue placeholder="Choose a land parcel" />
                    </SelectTrigger>
                    <SelectContent>
                      {lands.map((land) => (
                        <SelectItem key={land.land_id} value={land.land_id}>
                          {land.title} - {land.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title">Project Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g., My Dream Home"
                    required
                    data-testid="project-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Project details..."
                    rows={3}
                    data-testid="project-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="project_type">Project Type</Label>
                    <Select 
                      value={formData.project_type} 
                      onValueChange={(v) => handleSelectChange('project_type', v)}
                    >
                      <SelectTrigger data-testid="project-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new_build">New Build</SelectItem>
                        <SelectItem value="renovation">Renovation</SelectItem>
                        <SelectItem value="extension">Extension</SelectItem>
                        <SelectItem value="repair">Repair</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="total_budget">Budget (BWP)</Label>
                    <Input
                      id="total_budget"
                      name="total_budget"
                      type="number"
                      value={formData.total_budget}
                      onChange={handleInputChange}
                      placeholder="500000"
                      min="0"
                      data-testid="project-budget-input"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting || !formData.land_id} data-testid="submit-project-btn">
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Project'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* No lands message */}
        {lands.length === 0 && !loading && (
          <Card className="border border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <p className="text-amber-800">
                You need to register a land parcel before creating a project.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Projects List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : projects.length > 0 ? (
          <div className="space-y-4">
            {projects.map((project) => (
              <Card 
                key={project.project_id} 
                className="border border-border"
                data-testid={`project-card-${project.project_id}`}
              >
                <CardContent className="p-6">
                  {/* Project Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#0F2640] rounded-sm flex items-center justify-center shrink-0">
                        <FolderKanban className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-lg">{project.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {getLandTitle(project.land_id)} • {project.project_type.replace('_', ' ')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(project.status)}`}>
                        {project.status.replace('_', ' ')}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedProject(
                          expandedProject === project.project_id ? null : project.project_id
                        )}
                        data-testid={`toggle-stages-${project.project_id}`}
                      >
                        {expandedProject === project.project_id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">{calculateProgress(project.stages)}%</span>
                    </div>
                    <Progress value={calculateProgress(project.stages)} className="h-2" />
                  </div>

                  {/* Budget Info */}
                  {project.total_budget > 0 && (
                    <div className="flex items-center justify-between text-sm mb-4 p-3 bg-muted/30 rounded-sm">
                      <span className="text-muted-foreground">Budget</span>
                      <span className="font-mono font-medium">
                        BWP {project.total_budget.toLocaleString()}
                      </span>
                    </div>
                  )}

                  {/* Stages (Expanded) */}
                  {expandedProject === project.project_id && (
                    <div className="mt-6 pt-6 border-t border-border">
                      <h4 className="font-heading font-semibold mb-4 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Construction Stages
                      </h4>
                      <div className="space-y-3">
                        {project.stages?.map((stage, index) => (
                          <div
                            key={stage.stage_id}
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-sm"
                            data-testid={`stage-${stage.stage_id}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-xs font-mono text-muted-foreground w-6">
                                {String(index + 1).padStart(2, '0')}
                              </span>
                              {getStatusIcon(stage.status)}
                              <div>
                                <p className="font-medium">{stage.name}</p>
                                {stage.description && (
                                  <p className="text-xs text-muted-foreground">{stage.description}</p>
                                )}
                              </div>
                            </div>
                            <Select
                              value={stage.status}
                              onValueChange={(v) => handleStageUpdate(project.project_id, stage.stage_id, v)}
                            >
                              <SelectTrigger className="w-32" data-testid={`stage-status-${stage.stage_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border border-border">
            <CardContent className="p-12 text-center">
              <FolderKanban className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading font-bold text-lg mb-2">No projects yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first construction project
              </p>
              {lands.length > 0 && (
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Project
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProjectsPage;
